import { AgencyPerformanceResponse, UsageAnalyticsResponse, UsageBreakDownResponse } from './types';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { BusinessProfileResponse } from 'apis/business/types';
import { queryClient } from 'providers/AllProviders';
import { useParams } from 'react-router-dom';
import {
  createBusiness,
  getAgencyPerformance,
  getAgencyUsageAnalytics,
  getAgencyUsageBreakdown,
  getBusinesses,
  getDeleteBusiness,
  updateBusiness
} from './api';

export function useGetBusinesses(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: BusinessProfileResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: BusinessProfileResponse[]; meta: MetaApiResponse }>({
    queryKey: ['businesses', params],
    queryFn: () => getBusinesses(params),
    ...options
  });
}

export function useDeleteBusiness() {
  return useMutation({
    mutationFn: (id: number) => getDeleteBusiness(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['businesses'],
        refetchType: 'active'
      });
    }
  });
}

export function useCreateBusiness(handleClose: () => void) {
  return useMutation({
    mutationFn: (payload: FormData) => createBusiness(payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['businesses'],
          refetchType: 'active'
        });
        handleClose();
      }
    }
  });
}

export function useUpdateBusiness() {
  const { businessId } = useParams<{ businessId: string }>();
  return useMutation({
    mutationFn: (payload: FormData) => updateBusiness(Number(businessId), payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['businesses'],
          refetchType: 'active'
        });
      }
    }
  });
}

export function useGetAgencyPerformance(options?: UseQueryOptions<AgencyPerformanceResponse, Error>) {
  return useQuery<AgencyPerformanceResponse>({
    queryKey: ['agencyPerformance'],
    queryFn: getAgencyPerformance,
    ...options
  });
}

export function useGetAgencyUsageAnalytics(
  params?: Record<string, any>,
  options?: UseQueryOptions<UsageAnalyticsResponse, Error>
) {
  return useQuery<UsageAnalyticsResponse>({
    queryKey: ['usage_analytics', params],
    queryFn: () => getAgencyUsageAnalytics(params),
    ...options
  });
}

export function useGetAgencyUsageBreakdown(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: UsageBreakDownResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: UsageBreakDownResponse[]; meta: MetaApiResponse }>({
    queryKey: ['usage_breakdown', params],
    queryFn: () => getAgencyUsageBreakdown(params),
    ...options
  });
}
